import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleDashedIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  FilesIcon,
  FolderIcon,
  HourglassIcon,
  MinusIcon,
  PaperclipIcon,
  PlusIcon,
  XCircleIcon,
  XIcon,
  type Icon,
} from '@phosphor-icons/react';
import { forwardRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { captureException } from '@sentry/react';

import classNames from '@utils/classnames';
import { Alert } from '../../../components/alert/Alert';
import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { Pill } from '../../../components/pill/Pill';
import { useTranslation } from '../../../contexts/translation-context';
import { formatDate, formatDateTime, sortDate } from '../../../utils/date';
import { getDisplayError } from '../../../utils/get-display-error';
import { runPdfJobAndRefresh } from '../../../utils/pdf-job-client';
import { downloadUrl } from '../../../utils/download';
import { trackEvent } from '../../../utils/track-event';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import { MultiFileUploaderButton } from '../../document/components/MultiFileUploaderButton';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import {
  IncomingDocumentRejectionReasonToText,
  IncomingDocumentStatusToColor,
  IncomingDocumentStatusToText,
  IncomingDocumentTypeToText,
} from '../../incoming-document/utils/status';
import {
  IncomingDocumentRejectionReason,
  IncomingDocumentStatus,
  IncomingDocumentType,
  OrderDocumentType,
  OrderDocumentValidationStatus,
  OrderOutstandingDocumentState,
  OrderStatus,
} from 'src/types/enums';
import type { OrderDocumentTypeValue, OrderDocumentValidationStatusValue } from '../endpoints/shared.types';
import {
  generateOrderConfirmation,
  generateSustainabilityReport,
  linkOrderDocument,
  setOrderDocumentValidationStatus,
  unlinkOrderDocument,
} from '../models';
import { IncomingOrderEmailButton } from '../pages/order/IncomingOrderEmail';
import type { Order } from '../pages/order/Order';
import { useOrder } from '../pages/order/orderContext';
import { OrderDocumentTypeToTranslationKey } from '../utils/document-validation';
import { DismissDocValidationDialog } from './DismissDocValidationDialog';
import { getSelfBillEligibilityStatus, type SelfBillIneligibilityReason } from './OrderSelfBillEligibilityNote';
import {
  CMR_BUNDLE_DOC_TYPES,
  OrderCmrBundleDialog,
  getCmrBundleUrl,
  hasCmrBundleDocuments,
} from './OrderCmrBundleDialog';

type DocumentRef = Order['documents'][number]['document'];

const EXPANDED_STORAGE_KEY = 'tms.order-documents-bar.expanded';

function readExpanded(): boolean {
  try {
    return globalThis.localStorage.getItem(EXPANDED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeExpanded(expanded: boolean) {
  try {
    globalThis.localStorage.setItem(EXPANDED_STORAGE_KEY, expanded ? '1' : '0');
  } catch {
    // Storage is a convenience only.
  }
}

// ── Status line ──────────────────────────────────────────────────────────────

type Tone = 'positive' | 'warning' | 'negative' | 'muted';

const TONE_CLASS: Record<Tone, string> = {
  positive: 'text-positive-text',
  warning: 'text-warning-text',
  negative: 'text-negative-text',
  muted: 'text-fg-muted',
};

const OUTSTANDING_STATE_TEXT: Record<OrderOutstandingDocumentState, string> = {
  [OrderOutstandingDocumentState.Pending]: 'in verwerking',
  [OrderOutstandingDocumentState.Rejected]: 'afgekeurd',
  [OrderOutstandingDocumentState.Missing]: 'ontbreekt',
};

interface StatusLine {
  tone: Tone;
  icon: Icon;
  /** Short headline, fits on one line. */
  text: string;
  /** Per-document detail; shown in a tooltip and in the expanded details. */
  details?: string[];
}

/** Latest rejection reasons the analyzer gave for an incoming document of this type. */
function getRejectionReasons(order: Order, type: OrderDocumentType): string | null {
  const rejected = (order.incomingDocuments ?? [])
    .filter((d) => (d.type as string) === (type as string) && d.status === IncomingDocumentStatus.Rejected)
    .sort((a, b) => sortDate(b.date, a.date))[0];
  if (!rejected || !rejected.rejectionReasons.length) return null;
  return rejected.rejectionReasons.map((r) => IncomingDocumentRejectionReasonToText[r].toLowerCase()).join(', ');
}

// ── Rows ─────────────────────────────────────────────────────────────────────

type RowStatus = 'present' | 'pending' | 'rejected' | 'missing' | 'notApplicable' | 'attachment';

const ROW_STATUS_ICON: Record<RowStatus, { icon: Icon; className: string }> = {
  present: { icon: CheckIcon, className: 'text-positive-text' },
  pending: { icon: HourglassIcon, className: 'text-warning-text' },
  rejected: { icon: XIcon, className: 'text-negative-text' },
  missing: { icon: CircleDashedIcon, className: 'text-fg-muted' },
  notApplicable: { icon: MinusIcon, className: 'text-fg-muted' },
  attachment: { icon: PaperclipIcon, className: 'text-fg-secondary' },
};

const INCOMING_STATUS_TO_ROW_STATUS: Record<IncomingDocumentStatus, RowStatus> = {
  [IncomingDocumentStatus.Approved]: 'present',
  [IncomingDocumentStatus.New]: 'pending',
  [IncomingDocumentStatus.Pending]: 'pending',
  [IncomingDocumentStatus.Rejected]: 'rejected',
};

interface DocRowProps {
  status: RowStatus;
  icon?: Icon;
  name: string;
  meta: string;
  actions?: React.ReactNode;
}

const DocRow: React.FC<DocRowProps> = ({ status, icon, name, meta, actions }) => {
  const glyph = ROW_STATUS_ICON[status];
  const StatusIcon = icon ?? glyph.icon;
  const muted = status === 'missing' || status === 'notApplicable';

  return (
    <div className="flex min-h-[39px] items-center gap-2.5">
      <StatusIcon className={classNames('h-3.5 w-3.5 shrink-0', glyph.className)} weight="bold" />
      <div className="min-w-0 flex-1">
        <div className={classNames('truncate text-xs', muted ? 'text-fg-muted' : 'font-medium text-fg')}>{name}</div>
        <div className="truncate text-2xs text-fg-muted">{meta}</div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </div>
  );
};

const DocGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="min-w-0">
    <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-muted">{title}</div>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

interface RowActionProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  icon?: React.ReactElement;
  onTrigger?: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** `secondary` for the one thing you would do on an empty row (upload, generate). */
  color?: 'ghost' | 'secondary';
}

/**
 * Compact inline action, used both directly and as a dialog trigger. Forwards
 * the ref and the remaining props (onClick, aria-*) so Radix `Trigger asChild`
 * can drive it.
 */
const RowAction = forwardRef<HTMLButtonElement, RowActionProps>(
  ({ children, icon, onTrigger, isDisabled, isLoading, color = 'ghost', onClick, ...rest }, ref) => (
    <BaseButton
      {...rest}
      ref={ref}
      size={6}
      color={color}
      iconLeft={icon}
      onClick={(evt) => {
        onClick?.(evt);
        onTrigger?.();
      }}
      disabled={isDisabled}
      isLoading={isLoading}
    >
      {children}
    </BaseButton>
  ),
);

interface Props {
  order: Order;
}

/**
 * One bar above the order tabs holding every order document (TRA-170).
 * Collapsed: title, a one-line status of the required documents and the three
 * quick buttons (ODB, TRO, CMR bundle). Expanded: every document with its full
 * name grouped by origin, with open / replace / upload / generate on the row.
 */
export const OrderDocumentsBar: React.FC<Props> = ({ order }) => {
  const { refreshData } = useOrder();
  const { i18n } = useTranslation();
  const { downloadFile, downloadingFiles } = useDownloadDocument();
  const [expanded, setExpanded] = useState(readExpanded);
  const [isSettingStatus, setIsSettingStatus] = useState(false);

  const toggle = () => {
    setExpanded((prev) => {
      writeExpanded(!prev);
      return !prev;
    });
  };

  const isExecuted = order.status === OrderStatus.Executed;
  const isCancelled = order.status === OrderStatus.Cancelled;
  const disableUpload = isCancelled;
  const outstanding = order.outstandingRequiredDocuments;
  const manuallyApproved =
    order.documentValidationManuallySet && order.documentValidationStatus === OrderDocumentValidationStatus.Approved;

  const docName = (type: OrderDocumentType) => i18n(OrderDocumentTypeToTranslationKey[type]);
  const findDoc = (type: OrderDocumentType): DocumentRef | undefined =>
    order.documents.find((d) => d.type === type)?.document;
  const findDocs = (type: OrderDocumentType): DocumentRef[] =>
    order.documents.filter((d) => d.type === type).map((d) => d.document);

  // CMR is implicitly always required (matches cmr-bundle.server.ts).
  const requiredTypes = new Set<OrderDocumentType>([
    OrderDocumentType.Cmr,
    ...((order.customer?.requiredDocuments ?? []) as OrderDocumentType[]),
  ]);

  const statusLine = ((): StatusLine => {
    if (isCancelled) {
      return { tone: 'muted', icon: MinusIcon, text: 'Geen documenten verwacht — order geannuleerd' };
    }
    if (!isExecuted) {
      return { tone: 'muted', icon: MinusIcon, text: 'Nog geen documenten verwacht — order niet uitgevoerd' };
    }
    if (manuallyApproved) {
      return { tone: 'positive', icon: CheckCircleIcon, text: 'Documentcontrole manueel goedgekeurd' };
    }
    if (!outstanding.length) {
      return { tone: 'positive', icon: CheckCircleIcon, text: 'Alle vereiste documenten geleverd' };
    }
    const hasRejected = outstanding.some((d) => d.status === OrderOutstandingDocumentState.Rejected);
    const parts = [...outstanding]
      .sort(
        (a, b) =>
          Number(b.status === OrderOutstandingDocumentState.Rejected) -
          Number(a.status === OrderOutstandingDocumentState.Rejected),
      )
      .map((d) => {
        const name = `${docName(d.type as OrderDocumentType)}${d.count ? ` ${d.count.received}/${d.count.required}` : ''}`;
        if (d.status === OrderOutstandingDocumentState.Rejected) {
          const reasons = getRejectionReasons(order, d.type as OrderDocumentType);
          return `${name} afgekeurd — ${reasons ? `${reasons}, ` : ''}opnieuw uploaden`;
        }
        return `${name} ${OUTSTANDING_STATE_TEXT[d.status]}`;
      });
    return {
      tone: hasRejected ? 'negative' : 'warning',
      icon: hasRejected ? XCircleIcon : HourglassIcon,
      text: outstanding.length > 1 ? 'Te bezorgen documenten incompleet' : 'Te bezorgen document incompleet',
      details: parts,
    };
  })();
  const StatusIcon = statusLine.icon;

  // Self-billing eligibility is the headline status (it already implies the
  // documents are in order); the document line is added only when something
  // still needs attention, or when self-billing has nothing to say.
  const selfBillStatus = getSelfBillEligibilityStatus(
    order.selfBillEligibility.eligible,
    order.selfBillEligibility.reasons as SelfBillIneligibilityReason[],
    order.supplier?.billingCadence ?? null,
  );
  const SelfBillIcon = selfBillStatus?.icon;
  const showDocumentStatus = !selfBillStatus || statusLine.tone === 'warning' || statusLine.tone === 'negative';

  const setValidationStatus = async (status: OrderDocumentValidationStatus | null, note?: string) => {
    setIsSettingStatus(true);
    try {
      await setOrderDocumentValidationStatus(order.id, {
        status: status as OrderDocumentValidationStatusValue | null,
        note: note || undefined,
      });
      trackEvent(
        status === null ? 'order_documents_validation_reset_to_automatic' : 'order_documents_dismissed_from_banner',
        { order_id: order.id, status },
      );
      toast.success('Status bijgewerkt');
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error(`Kon status niet bijwerken: ${getDisplayError(err)}`);
    } finally {
      setIsSettingStatus(false);
    }
  };

  const linkMany = (type: OrderDocumentType) => async (documents: Array<{ id: string; name: string }>) => {
    let count = 0;
    await Promise.allSettled(
      documents.map(async (doc) => {
        try {
          await linkOrderDocument(order.id, { documentId: doc.id, type: type as OrderDocumentTypeValue });
          count += 1;
          toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
        } catch (err) {
          captureException(err);
          toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
        }
      }),
    );
    refreshData();
  };

  const unlinkOne = (type: OrderDocumentType) => async (documentId: string) => {
    try {
      await unlinkOrderDocument(order.id, { documentId, type: type as OrderDocumentTypeValue });
      toast.success('Bestand gewist');
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
    }
  };

  const openAction = (file: DocumentRef, label = 'Openen') => (
    <RowAction
      key="open"
      icon={<DownloadSimpleIcon className="button-icon" />}
      onTrigger={() => downloadFile(file)}
      isLoading={downloadingFiles.includes(file.id)}
    >
      {label}
    </RowAction>
  );

  // ── Generated documents (ODB / SR) ──
  const generatedRow = (
    type: OrderDocumentType.OrderConfirmation | OrderDocumentType.SustainabilityReport,
    title: string,
    generate: () => Promise<void>,
  ) => {
    const file = findDoc(type);
    const dialog = (trigger: React.ReactElement) => (
      <FileUploaderButton
        key="dialog"
        buttonText={title}
        title={title}
        file={file}
        generateDocument={generate}
        disableUpload
        trigger={trigger}
      />
    );
    return (
      <DocRow
        status={file ? 'present' : 'missing'}
        name={docName(type)}
        meta={file ? file.name : 'Nog niet aangemaakt'}
        actions={
          file ? (
            <>
              {openAction(file)}
              {dialog(<RowAction title="Document opnieuw genereren">Hergenereren</RowAction>)}
            </>
          ) : (
            dialog(
              <RowAction color="secondary" icon={<PlusIcon className="button-icon" />}>
                Genereren
              </RowAction>,
            )
          )
        }
      />
    );
  };

  const generateConfirmation = async () => {
    await runPdfJobAndRefresh(await generateOrderConfirmation(order.id), async () => {
      refreshData();
    });
    trackEvent('order_confirmation_document_generated', { order_id: order.id });
  };
  const generateSustainability = async () => {
    await runPdfJobAndRefresh(await generateSustainabilityReport(order.id), async () => {
      refreshData();
    });
    trackEvent('order_sustainability_report_generated', { order_id: order.id });
  };

  // ── System documents that only exist once produced elsewhere (TRO / planning / purchase invoice) ──
  const readOnlyRow = (name: string, file: DocumentRef | null | undefined, emptyMeta: string) => (
    <DocRow
      status={file ? 'present' : 'notApplicable'}
      name={name}
      meta={file ? file.name : emptyMeta}
      actions={file ? openAction(file) : undefined}
    />
  );

  // ── Transport documents (CMR / weigh slip / delivery note / pallet note) ──
  const transportRow = ({ type, namePrefix }: (typeof CMR_BUNDLE_DOC_TYPES)[number]) => {
    const file = findDoc(type);
    const pending = outstanding.find((d) => d.type === type);
    const required = requiredTypes.has(type);

    // An order unloading at several places needs a CMR per place, so a linked
    // CMR can still be incomplete.
    const perPlace = pending?.count
      ? `${pending.count.received} van ${pending.count.required} ontvangen (één per losplaats)`
      : null;

    let status: RowStatus;
    let meta: string;
    if (file && !perPlace) {
      status = 'present';
      const received = (order.incomingDocuments ?? [])
        .filter((d) => (d.type as string) === (type as string) && d.status === IncomingDocumentStatus.Approved)
        .sort((a, b) => sortDate(b.date, a.date))[0];
      meta = received ? `Ontvangen ${formatDate(received.date)}` : file.name;
    } else if (pending?.status === OrderOutstandingDocumentState.Pending) {
      status = 'pending';
      meta = 'In verwerking';
    } else if (pending?.status === OrderOutstandingDocumentState.Rejected) {
      status = 'rejected';
      const reasons = getRejectionReasons(order, type);
      meta = reasons ? `Afgekeurd — ${reasons}` : 'Afgekeurd';
    } else if (!required) {
      status = 'notApplicable';
      meta = 'Niet vereist';
    } else if (isExecuted) {
      status = 'missing';
      meta = 'Ontbreekt';
    } else {
      status = 'missing';
      meta = 'Nog niet verwacht';
    }
    if (perPlace) meta = `${meta} · ${perPlace}`;

    // `append` adds the upload to the linked PDF instead of replacing it: the
    // way to hand in the CMR for a remaining unload place.
    const dialog = (trigger: React.ReactElement, { append = false } = {}) => (
      <FileUploaderButton
        key={append ? 'append' : 'dialog'}
        buttonText={docName(type)}
        title={docName(type)}
        initialName={`${namePrefix} ${order.orderNumber ?? ''}`}
        file={append ? undefined : file}
        onSubmit={async (document) => {
          await linkOrderDocument(order.id, {
            documentId: document.id,
            type: type as OrderDocumentTypeValue,
            shouldMerge: append,
          });
          refreshData();
        }}
        unlinkDocument={file && !append ? () => unlinkOne(type)(file.id) : undefined}
        disableUpload={disableUpload}
        trigger={trigger}
      />
    );

    return (
      <DocRow
        key={type}
        status={status}
        name={docName(type)}
        meta={meta}
        actions={
          file ? (
            <>
              {openAction(file)}
              {perPlace &&
                !disableUpload &&
                dialog(
                  <RowAction
                    color="secondary"
                    icon={<PlusIcon className="button-icon" />}
                    title="CMR toevoegen aan het bestaande document"
                  >
                    Toevoegen
                  </RowAction>,
                  { append: true },
                )}
              {dialog(<RowAction title="Document vervangen of verwijderen">Vervangen</RowAction>)}
            </>
          ) : disableUpload ? undefined : (
            dialog(
              <RowAction color="secondary" icon={<PlusIcon className="button-icon" />}>
                Uploaden
              </RowAction>,
            )
          )
        }
      />
    );
  };

  // ── Multi-file attachments (customer attachments / other) ──
  type AttachmentType = OrderDocumentType.CustomerAttachment | OrderDocumentType.Other;
  const attachmentsDialog = (type: AttachmentType, name: string, key: string, trigger: React.ReactElement) => (
    <MultiFileUploaderButton
      key={key}
      buttonText={name}
      title={name}
      files={findDocs(type)}
      onSubmit={linkMany(type)}
      unlinkDocument={unlinkOne(type)}
      disableUpload={disableUpload}
      trigger={trigger}
    />
  );
  const attachmentsRow = (type: AttachmentType, name: string) => {
    const files = findDocs(type);
    const dialog = (key: string, trigger: React.ReactElement) => attachmentsDialog(type, name, key, trigger);
    return (
      <DocRow
        status={files.length ? 'attachment' : 'missing'}
        name={name}
        meta={files.length ? `${files.length} ${files.length === 1 ? 'bestand' : 'bestanden'}` : 'Geen bestanden'}
        actions={
          files.length ? (
            <>
              {dialog('open', <RowAction icon={<DownloadSimpleIcon className="button-icon" />}>Openen</RowAction>)}
              {!disableUpload &&
                dialog(
                  'add',
                  <RowAction title="Extra bestand toevoegen" icon={<PlusIcon className="button-icon" />}>
                    Toevoegen
                  </RowAction>,
                )}
            </>
          ) : disableUpload ? undefined : (
            dialog(
              'upload',
              <RowAction color="secondary" icon={<PlusIcon className="button-icon" />}>
                Uploaden
              </RowAction>,
            )
          )
        }
      />
    );
  };

  // ── Quick buttons: every document that exists, one click away while collapsed ──
  const odbFile = findDoc(OrderDocumentType.OrderConfirmation);
  const troFile = findDoc(OrderDocumentType.TransportOrder);
  const planningFile = findDoc(OrderDocumentType.Planning);
  const customerAttachments = findDocs(OrderDocumentType.CustomerAttachment);
  const otherAttachments = findDocs(OrderDocumentType.Other);
  const quickIcon = <ArrowSquareOutIcon className="button-icon" />;

  const quickOpen = (key: string, label: string, file: DocumentRef, title: string) => (
    <Button
      key={key}
      size={8}
      iconLeft={quickIcon}
      onTrigger={() => downloadFile(file)}
      isLoading={downloadingFiles.includes(file.id)}
      title={title}
    >
      {label}
    </Button>
  );
  const quickAttachments = (type: AttachmentType, name: string, count: number) =>
    attachmentsDialog(
      type,
      name,
      `quick-${type}`,
      <BaseButton size={8} iconLeft={<PaperclipIcon className="button-icon" />} title={`${name} openen`}>
        {`${name} (${count})`}
      </BaseButton>,
    );

  const quickButtons = [
    odbFile && quickOpen('odb', 'ODB', odbFile, 'Orderbevestiging openen'),
    troFile && quickOpen('tro', 'TRO', troFile, 'Transportopdracht openen'),
    planningFile && quickOpen('planning', 'Planning', planningFile, 'Planning openen'),
    customerAttachments.length > 0 &&
      quickAttachments(OrderDocumentType.CustomerAttachment, 'Bijlagen klant', customerAttachments.length),
    otherAttachments.length > 0 && quickAttachments(OrderDocumentType.Other, 'Andere', otherAttachments.length),
  ].filter(Boolean);

  const email = order.incomingOrderEmail[0];

  // Hide auto-rejected transport orders — pure noise (we sent them ourselves
  // and the analyzer round-trips them as Rejected/WrongDocumentType).
  const incoming = (order.incomingDocuments ?? []).filter((doc) => {
    if (doc.type !== IncomingDocumentType.TransportOrder) return true;
    return !doc.rejectionReasons.includes(IncomingDocumentRejectionReason.WrongDocumentType);
  });

  // Same rule the old Documents tab used for "Manueel goedkeuren", widened with
  // the executed-with-outstanding-docs case the banner covered.
  const canApproveManually =
    !order.documentValidationManuallySet &&
    (order.documentValidationStatus !== OrderDocumentValidationStatus.Approved ||
      (isExecuted && outstanding.length > 0));
  const showValidationActions =
    order.documentValidationManuallySet || canApproveManually || Boolean(selfBillStatus?.details?.length);
  const hasBundle = hasCmrBundleDocuments(order);

  return (
    <div className="card my-4">
      <div className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
        <FolderIcon className="h-4 w-4 shrink-0 text-fg-secondary" />
        <span className="text-sm font-semibold text-fg">Documenten</span>
        {selfBillStatus && SelfBillIcon && (
          <span
            className={classNames(
              'flex min-w-0 items-center gap-1.5 text-xs font-medium',
              TONE_CLASS[selfBillStatus.tone],
            )}
            title={
              selfBillStatus.details
                ? `${selfBillStatus.text}: ${selfBillStatus.details.join(' · ')}`
                : selfBillStatus.text
            }
          >
            <SelfBillIcon className="h-3.5 w-3.5 shrink-0" weight="bold" />
            <span className="truncate">{selfBillStatus.text}</span>
          </span>
        )}
        {showDocumentStatus && (
          <span
            className={classNames('flex min-w-0 items-center gap-1.5 text-xs font-medium', TONE_CLASS[statusLine.tone])}
            title={statusLine.details ? `${statusLine.text}: ${statusLine.details.join(' · ')}` : statusLine.text}
          >
            <StatusIcon className="h-3.5 w-3.5 shrink-0" weight="bold" />
            <span className="truncate">{statusLine.text}</span>
          </span>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {quickButtons}
          <OrderCmrBundleDialog
            order={order}
            trigger={
              <BaseButton size={8} iconLeft={quickIcon} title="CMR-bundel en onderdelen">
                CMR
              </BaseButton>
            }
          />
          <div className="mx-1.5 h-[22px] w-px bg-border" />
          <Button
            size={8}
            color="ghost"
            onTrigger={toggle}
            iconRight={
              expanded ? (
                <CaretUpIcon className="button-icon" weight="bold" />
              ) : (
                <CaretDownIcon className="button-icon" weight="bold" />
              )
            }
          >
            {expanded ? 'Verberg details' : 'Toon details'}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 pt-3.5">
          <div className="grid gap-x-7 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
            <DocGroup title="Aangemaakt door TransDirect">
              {generatedRow(OrderDocumentType.OrderConfirmation, 'Order bevestiging', generateConfirmation)}
              {generatedRow(OrderDocumentType.SustainabilityReport, 'Sustainability Report', generateSustainability)}
              {readOnlyRow(docName(OrderDocumentType.TransportOrder), troFile, 'Nog niet aangemaakt')}
              {readOnlyRow(docName(OrderDocumentType.Planning), planningFile, 'Nog niet aangemaakt')}
            </DocGroup>

            <DocGroup title="Vervoersdocumenten">
              {CMR_BUNDLE_DOC_TYPES.map(transportRow)}
              <DocRow
                status={hasBundle ? 'attachment' : 'missing'}
                icon={hasBundle ? FilesIcon : undefined}
                name="CMR-bundel"
                meta={hasBundle ? 'Alle vervoersdocumenten als één PDF' : 'Nog geen vervoersdocumenten ontvangen'}
                actions={
                  hasBundle ? (
                    <RowAction
                      icon={<DownloadSimpleIcon className="button-icon" />}
                      onTrigger={() => downloadUrl(getCmrBundleUrl(order.id), { openInNewTab: true })}
                    >
                      Openen
                    </RowAction>
                  ) : undefined
                }
              />
            </DocGroup>

            <DocGroup title="Bijlagen">
              {readOnlyRow('Aankoopfactuur', order.purchase?.document, 'Geen aankoopfactuur gekoppeld')}
              {attachmentsRow(OrderDocumentType.CustomerAttachment, 'Bijlagen klant')}
              {attachmentsRow(OrderDocumentType.Other, 'Andere')}
              {email && (
                <DocRow
                  status="attachment"
                  icon={EnvelopeSimpleIcon}
                  name="Order e-mail"
                  meta={email.subject || email.from || 'Inkomende e-mail'}
                  actions={
                    <IncomingOrderEmailButton
                      email={email}
                      trigger={<RowAction icon={<DownloadSimpleIcon className="button-icon" />}>Openen</RowAction>}
                    />
                  }
                />
              )}
            </DocGroup>
          </div>

          {incoming.length > 0 && (
            <div className="mt-4 border-t border-border pt-3.5">
              <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-muted">
                Inkomende documenten ({incoming.length})
              </div>
              <div className="flex flex-col gap-0.5">
                {incoming.map((doc) => {
                  const glyph = ROW_STATUS_ICON[INCOMING_STATUS_TO_ROW_STATUS[doc.status]];
                  const IncomingIcon = glyph.icon;
                  return (
                    <div key={doc.id} className="flex min-h-[39px] items-center gap-2.5">
                      <IncomingIcon className={classNames('h-3.5 w-3.5 shrink-0', glyph.className)} weight="bold" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs font-medium text-fg">
                          <span className="truncate">{IncomingDocumentTypeToText[doc.type]}</span>
                          <Pill color={IncomingDocumentStatusToColor[doc.status]} size="sm">
                            {IncomingDocumentStatusToText[doc.status]}
                          </Pill>
                        </div>
                        <div className="truncate text-2xs text-fg-muted">
                          {[
                            doc.subject || '(geen onderwerp)',
                            doc.from ? `Van: ${doc.from}` : null,
                            formatDateTime(doc.date),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                        {doc.rejectionReasons.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {doc.rejectionReasons.map((reason) => (
                              <Pill key={reason} color="negative" size="sm">
                                {IncomingDocumentRejectionReasonToText[reason]}
                              </Pill>
                            ))}
                          </div>
                        )}
                      </div>
                      <RowAction
                        icon={<DownloadSimpleIcon className="button-icon" />}
                        title={doc.document.name}
                        onTrigger={() => downloadFile(doc.document)}
                        isLoading={downloadingFiles.includes(doc.document.id)}
                      >
                        Openen
                      </RowAction>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showValidationActions && (
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3">
              {selfBillStatus?.details && selfBillStatus.details.length > 0 && (
                <Alert type="info" className="w-full" title={selfBillStatus.text}>
                  <ul className="ml-5 list-disc">
                    {selfBillStatus.details.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {statusLine.details && statusLine.details.length > 0 && (
                <Alert
                  type={statusLine.tone === 'negative' ? 'error' : 'warning'}
                  className="w-full"
                  title={statusLine.text}
                >
                  <ul className="ml-5 list-disc">
                    {statusLine.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {order.documentValidationManuallySet && order.documentValidationNote && (
                <Alert type="neutral" className="w-full">
                  <span className="font-medium">Documentvalidatie opmerking:</span> {order.documentValidationNote}
                </Alert>
              )}
              <div className="ml-auto flex items-center gap-2">
                {canApproveManually && (
                  <DismissDocValidationDialog
                    onSubmit={async (note) => {
                      await setValidationStatus(OrderDocumentValidationStatus.Approved, note);
                    }}
                  />
                )}
                {order.documentValidationManuallySet && (
                  <Button
                    size={8}
                    isDisabled={isSettingStatus}
                    onTrigger={async () => {
                      await setValidationStatus(null);
                    }}
                  >
                    Reset naar automatisch
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
